/**
 * Store Assignment Utility Functions
 * 
 * Provides helper functions for managing store assignments to users.
 * Supports both auto-assignment (by salesGroup) and manual assignment.
 */

import { Store } from '../@types/store';
import { UserProfile } from '../@types/user';

/**
 * Normalize salesGroup for case-insensitive comparison
 */
export const normalizeSalesGroup = (salesGroup?: string): string => {
  if (!salesGroup) return '';
  return salesGroup.trim().toLowerCase();
};

/**
 * Check if two salesGroups match (case-insensitive)
 */
export const salesGroupsMatch = (sg1?: string, sg2?: string): boolean => {
  if (!sg1 || !sg2) return false;
  return normalizeSalesGroup(sg1) === normalizeSalesGroup(sg2);
};

/**
 * Get stores assigned to a user
 * Includes both auto-assigned (by salesGroup) and manually assigned stores
 * Also checks user's assignedStoreIds field as a fallback
 */
export const getAssignedStores = async (
  userId: string,
  allStores: Store[],
  userSalesGroup?: string,
  userAssignedStoreIds?: string[]
): Promise<Store[]> => {
  if (!userId || !allStores) return [];

  const assignedStores = allStores.filter(store => {
    // Check manual assignment via store's assignedToUserIds (bidirectional relationship)
    const isManuallyAssignedViaStore = store.assignedToUserIds?.includes(userId);
    
    // Check manual assignment via user's assignedStoreIds (fallback for direct assignment)
    const isManuallyAssignedViaUser = userAssignedStoreIds?.includes(store.id);
    
    // Check auto-assignment by salesGroup
    const isAutoAssigned = userSalesGroup && 
      store.salesGroup && 
      salesGroupsMatch(store.salesGroup, userSalesGroup);

    return isManuallyAssignedViaStore || isManuallyAssignedViaUser || isAutoAssigned;
  });

  return assignedStores;
};

/**
 * Get stores NOT assigned to a user
 */
export const getUnassignedStores = async (
  userId: string,
  allStores: Store[],
  userSalesGroup?: string
): Promise<Store[]> => {
  if (!userId || !allStores) return [];

  const unassignedStores = allStores.filter(store => {
    // Check manual assignment
    const isManuallyAssigned = store.assignedToUserIds?.includes(userId);
    
    // Check auto-assignment by salesGroup
    const isAutoAssigned = userSalesGroup && 
      store.salesGroup && 
      salesGroupsMatch(store.salesGroup, userSalesGroup);

    return !isManuallyAssigned && !isAutoAssigned;
  });

  return unassignedStores;
};

/**
 * Get all stores (convenience function)
 */
export const getAllStores = async (allStores: Store[]): Promise<Store[]> => {
  return allStores || [];
};

/**
 * Check if a store is assigned to a user
 */
export const isStoreAssignedToUser = (
  store: Store,
  userId: string,
  userSalesGroup?: string
): boolean => {
  // Check manual assignment
  const isManuallyAssigned = store.assignedToUserIds?.includes(userId);
  
  // Check auto-assignment by salesGroup
  const isAutoAssigned = userSalesGroup && 
    store.salesGroup && 
    salesGroupsMatch(store.salesGroup, userSalesGroup);

  return isManuallyAssigned || isAutoAssigned;
};

/**
 * Get stores by salesGroup (case-insensitive)
 */
export const getStoresBySalesGroup = (
  allStores: Store[],
  salesGroup: string
): Store[] => {
  if (!salesGroup || !allStores) return [];

  return allStores.filter(store => 
    store.salesGroup && salesGroupsMatch(store.salesGroup, salesGroup)
  );
};

/**
 * Get stores with no salesGroup assigned
 */
export const getStoresWithoutSalesGroup = (allStores: Store[]): Store[] => {
  if (!allStores) return [];

  return allStores.filter(store => !store.salesGroup || store.salesGroup.trim() === '');
};

/**
 * Get stores manually assigned to a user (excludes auto-assignments)
 */
export const getManuallyAssignedStores = (
  allStores: Store[],
  userId: string
): Store[] => {
  if (!userId || !allStores) return [];

  return allStores.filter(store => 
    store.assignedToUserIds?.includes(userId)
  );
};

/**
 * Get stores auto-assigned to a user by salesGroup (excludes manual assignments)
 */
export const getAutoAssignedStores = (
  allStores: Store[],
  userSalesGroup?: string,
  userId?: string
): Store[] => {
  if (!userSalesGroup || !allStores) return [];

  return allStores.filter(store => {
    const matchesSalesGroup = store.salesGroup && 
      salesGroupsMatch(store.salesGroup, userSalesGroup);
    
    // Exclude if manually assigned (to avoid duplicates)
    const isManuallyAssigned = userId && store.assignedToUserIds?.includes(userId);
    
    return matchesSalesGroup && !isManuallyAssigned;
  });
};

/**
 * Sort stores by name (ascending)
 */
export const sortStoresByName = (stores: Store[]): Store[] => {
  return [...stores].sort((a, b) => 
    (a.StoreName || '').localeCompare(b.StoreName || '')
  );
};

/**
 * Sort stores by customer number (ascending)
 */
export const sortStoresByCustomerNo = (stores: Store[]): Store[] => {
  return [...stores].sort((a, b) => 
    (a.CustomerNo || '').localeCompare(b.CustomerNo || '')
  );
};

/**
 * Sort stores by city (ascending)
 */
export const sortStoresByCity = (stores: Store[]): Store[] => {
  return [...stores].sort((a, b) => 
    (a.City || '').localeCompare(b.City || '')
  );
};

/**
 * Filter stores by city (case-insensitive)
 */
export const filterStoresByCity = (stores: Store[], city: string): Store[] => {
  if (!city || !stores) return stores;
  
  const normalizedCity = city.toLowerCase();
  return stores.filter(store => 
    store.City?.toLowerCase().includes(normalizedCity)
  );
};

/**
 * Filter stores by province (case-insensitive)
 */
export const filterStoresByProvince = (stores: Store[], province: string): Store[] => {
  if (!province || !stores) return stores;
  
  const normalizedProvince = province.toLowerCase();
  return stores.filter(store => 
    store.Prov?.toLowerCase().includes(normalizedProvince)
  );
};

/**
 * Filter stores by search query (searches name, customer no, city, address)
 */
export const filterStoresByQuery = (stores: Store[], query: string): Store[] => {
  if (!query || !stores) return stores;
  
  const normalizedQuery = query.toLowerCase();
  return stores.filter(store => 
    store.StoreName?.toLowerCase().includes(normalizedQuery) ||
    store.CustomerNo?.toLowerCase().includes(normalizedQuery) ||
    store.City?.toLowerCase().includes(normalizedQuery) ||
    store.Address?.toLowerCase().includes(normalizedQuery) ||
    store.salesGroup?.toLowerCase().includes(normalizedQuery)
  );
};

/**
 * Get unique sales groups from stores
 */
export const getUniqueSalesGroups = (stores: Store[]): string[] => {
  if (!stores) return [];

  const salesGroups = stores
    .map(store => store.salesGroup)
    .filter((sg): sg is string => !!sg && sg.trim() !== '');

  return [...new Set(salesGroups)].sort();
};

/**
 * Get unique cities from stores
 */
export const getUniqueCities = (stores: Store[]): string[] => {
  if (!stores) return [];

  const cities = stores
    .map(store => store.City)
    .filter((city): city is string => !!city && city.trim() !== '');

  return [...new Set(cities)].sort();
};

/**
 * Get unique provinces from stores
 */
export const getUniqueProvinces = (stores: Store[]): string[] => {
  if (!stores) return [];

  const provinces = stores
    .map(store => store.Prov)
    .filter((prov): prov is string => !!prov && prov.trim() !== '');

  return [...new Set(provinces)].sort();
};

/**
 * Count stores by salesGroup
 */
export const countStoresBySalesGroup = (stores: Store[]): Record<string, number> => {
  if (!stores) return {};

  const counts: Record<string, number> = {};
  
  stores.forEach(store => {
    const sg = store.salesGroup || '(None)';
    counts[sg] = (counts[sg] || 0) + 1;
  });

  return counts;
};

/**
 * Get assignment summary for a user
 */
export const getAssignmentSummary = (
  userId: string,
  allStores: Store[],
  userSalesGroup?: string
) => {
  const manual = getManuallyAssignedStores(allStores, userId);
  const auto = getAutoAssignedStores(allStores, userSalesGroup, userId);
  
  return {
    total: manual.length + auto.length,
    manual: manual.length,
    auto: auto.length,
    manualStores: manual,
    autoStores: auto
  };
};

/**
 * Check if a store matches user's salesGroup (for visual indicators)
 */
export const storeMatchesUserSalesGroup = (
  store: Store,
  userSalesGroup?: string
): boolean => {
  if (!userSalesGroup || !store.salesGroup) return false;
  return salesGroupsMatch(store.salesGroup, userSalesGroup);
};

/**
 * Get stores that need assignment (no salesGroup and not manually assigned)
 */
export const getUnassignedStoresWithoutSalesGroup = (
  allStores: Store[]
): Store[] => {
  if (!allStores) return [];

  return allStores.filter(store => {
    const hasNoSalesGroup = !store.salesGroup || store.salesGroup.trim() === '';
    const hasNoManualAssignment = !store.assignedToUserIds || store.assignedToUserIds.length === 0;
    
    return hasNoSalesGroup && hasNoManualAssignment;
  });
};

/**
 * Validate store assignment data
 */
export const validateStoreAssignment = (
  storeId: string,
  userId: string,
  username: string
): { valid: boolean; error?: string } => {
  if (!storeId || storeId.trim() === '') {
    return { valid: false, error: 'Store ID is required' };
  }
  
  if (!userId || userId.trim() === '') {
    return { valid: false, error: 'User ID is required' };
  }
  
  if (!username || username.trim() === '') {
    return { valid: false, error: 'Username is required' };
  }
  
  return { valid: true };
};
